import { create } from "zustand";

// Suppresses the layout-level forced-password redirect (/complete-account)
// while a login/register modal is driving its own name/password steps, so the
// gate never hijacks an in-progress flow. Deliberately NOT persisted: a stale
// suppression must never survive a reload.
interface PasswordGateState {
  suppressCount: number;
  suppress: () => void;
  release: () => void;
}

export const usePasswordGateStore = create<PasswordGateState>()(set => ({
  suppressCount: 0,
  suppress: () => {
    set(state => ({ suppressCount: state.suppressCount + 1 }));
  },
  release: () => {
    set(state => ({ suppressCount: Math.max(0, state.suppressCount - 1) }));
  },
}));

// Where the gate interrupted the visitor, so /complete-account can send them
// back there (a deep link such as /plans/KH/?product=…&coupon-code=… must end
// in checkout, not on the home page) once the password is set. Session
// storage: it must survive the navigation to /complete-account and a reload
// there, but never a later browser session.
const RETURN_TO_KEY = "bnesim.password_gate_return_to";
const AUTH_ROUTE_SEGMENTS = [
  "/login",
  "/register",
  "/forgot-password",
  "/oauth",
  "/complete-account",
];

export interface GateReturnTo {
  /** Path without the language prefix. */
  pathname: string;
  /** Query string without the leading "?". */
  search: string;
}

export const rememberGateReturnTo = (
  pathname: string,
  search: string,
): void => {
  try {
    const params = new URLSearchParams(search);
    // A `?product=` hand-off that stopped at the login modal has already
    // consumed the plans page's one-shot auto-checkout guard; `checkout=1`
    // re-arms it so the trip back ends in checkout, where the visitor was
    // headed.
    if (params.has("product") && !params.has("checkout")) {
      params.set("checkout", "1");
    }
    const value: GateReturnTo = { pathname, search: params.toString() };
    window.sessionStorage.setItem(RETURN_TO_KEY, JSON.stringify(value));
  } catch {
    // Storage unavailable: /complete-account falls back to the home page.
  }
};

/** Reads and clears the remembered location; null when there is none or it is not a safe internal route. */
export const takeGateReturnTo = (): GateReturnTo | null => {
  try {
    const raw = window.sessionStorage.getItem(RETURN_TO_KEY);
    window.sessionStorage.removeItem(RETURN_TO_KEY);
    if (raw === null) return null;
    const value = JSON.parse(raw) as Partial<GateReturnTo>;
    const pathname = typeof value.pathname === "string" ? value.pathname : "";
    if (
      !pathname.startsWith("/") ||
      pathname.startsWith("//") ||
      AUTH_ROUTE_SEGMENTS.some(segment => pathname.startsWith(segment))
    ) {
      return null;
    }
    return {
      pathname,
      search: typeof value.search === "string" ? value.search : "",
    };
  } catch {
    return null;
  }
};

export const clearGateReturnTo = (): void => {
  try {
    window.sessionStorage.removeItem(RETURN_TO_KEY);
  } catch {
    // ignore
  }
};

/** "/de/plans/kh/" → "/plans/kh/" for any of the site's languages. */
export const stripLanguagePrefix = (
  pathname: string,
  languages: readonly string[],
): string => {
  for (const language of languages) {
    if (pathname === `/${language}` || pathname === `/${language}/`) return "/";
    if (pathname.startsWith(`/${language}/`)) {
      return pathname.slice(language.length + 1);
    }
  }
  return pathname;
};
